import useIsSmall from "@/hooks/useIsSmall";
import { FileRecord, UserAppointmentsDto, WorkScheduleListData } from "@/models/club";
import { ClickEventSlotProps } from "@/pages/dashboard/calendar/type";
import moment from "moment-jalaali";
import React, { useMemo } from "react";
import { EventItem } from "./EventItem";
import { getPartnerDayEvents, getShiftHours, hasShiftAtHour } from "./helper/helper";
import { TimeGrid, TimeGridColumn } from "./TimeGrid";
import { CalendarEvent, CalendarViewProps } from "./types";
import UserHeader from "./UserHeader";

const EMPTY_FILE_RECORD: FileRecord = {};

const DayHeader = ({ day, isPersian, isToday }: { day: moment.Moment; isPersian: boolean; isToday: boolean }) => (
  <div className="calendar-day-head" data-today={isToday || undefined}>
    <span className="calendar-day-head-name">{day.format("dddd")}</span>
    <span className="calendar-day-head-number">{isPersian ? day.format("jD") : day.format("D")}</span>
  </div>
);

// One member row of the multi-member week: member cell + one agenda cell per day.
const PartnerRow = React.memo(function PartnerRow({
  partner,
  weekDays,
  todayKey,
  shiftSchedule,
  onEmptySlotClick,
  onEventClick,
  setSelectedEvent,
}: {
  partner: UserAppointmentsDto;
  weekDays: moment.Moment[];
  todayKey: string;
  shiftSchedule?: WorkScheduleListData;
  onEmptySlotClick?: (props: ClickEventSlotProps) => void;
  onEventClick?: (event: CalendarEvent) => void;
  setSelectedEvent?: (event: CalendarEvent | null) => void;
}) {
  return (
    <>
      <div className="calendar-roster-member">
        <UserHeader
          layout="row"
          fileRecord={partner?.user?.image ?? EMPTY_FILE_RECORD}
          firstName={partner?.user?.firstName ?? ""}
          lastName={partner?.user?.lastName ?? ""}
          color={partner?.user?.calendarColor ?? ""}
          id={partner?.user?.id ?? 0}
        />
      </div>
      {weekDays.map((day) => {
        const dayEvents = getPartnerDayEvents(partner?.appointmentCalendar || [], day, partner);
        const hasShift = hasShiftAtHour(shiftSchedule || [], day, partner?.user?.id, 12); // Check midday shift
        return (
          <div
            key={day.format("YYYY-MM-DD")}
            className="calendar-roster-cell calendar-slot"
            data-today={day.format("YYYY-MM-DD") === todayKey || undefined}
            data-off={(!hasShift && dayEvents.length === 0) || undefined}
            onClick={() => onEmptySlotClick?.({ partner, hour: "12:00", date: day.clone() })}
          >
            {dayEvents.map((event, eventIndex) => (
              <EventItem key={`${event.id}-${eventIndex}`} view="month" event={event} onEventClick={onEventClick} setSelectedEvent={setSelectedEvent} />
            ))}
          </div>
        );
      })}
    </>
  );
});

const WeekView = ({ currentDate, partners, onEventClick, onEmptySlotClick, isPersian, shiftSchedule, setSelectedEvent }: CalendarViewProps) => {
  const isSmall = useIsSmall();
  const dayCount = isSmall ? 3 : 7;
  const weekDays = useMemo(() => {
    const start = currentDate || moment();
    return Array.from({ length: dayCount }, (_, i) => start.clone().add(i, "days"));
  }, [currentDate, dayCount]);
  const todayKey = moment().format("YYYY-MM-DD");
  const singlePartner = partners?.length === 1 ? partners[0] : undefined;

  const columns = useMemo<TimeGridColumn[]>(() => {
    if (!singlePartner) return [];
    return weekDays.map((day) => ({
      key: day.format("YYYY-MM-DD"),
      partner: singlePartner,
      date: day,
      isToday: day.format("YYYY-MM-DD") === todayKey,
      events: getPartnerDayEvents(singlePartner.appointmentCalendar, day, singlePartner),
      shiftsByHour: getShiftHours(shiftSchedule || [], day, singlePartner.user?.id),
    }));
  }, [singlePartner, weekDays, shiftSchedule, todayKey]);

  const headers = useMemo(
    () => weekDays.map((day) => <DayHeader day={day} isPersian={isPersian} isToday={day.format("YYYY-MM-DD") === todayKey} />),
    [weekDays, isPersian, todayKey]
  );

  if (!partners || partners.length === 0) return null;

  // Single member: time grid with one column per day.
  if (singlePartner) {
    return (
      <TimeGrid
        columns={columns}
        headers={headers}
        tintToday
        scrollKey={`${singlePartner.user?.id || 0}-${currentDate?.format("YYYY-MM-DD") || ""}-${dayCount}`}
        onEmptySlotClick={onEmptySlotClick}
        onEventClick={onEventClick}
        setSelectedEvent={setSelectedEvent}
      />
    );
  }

  // Multiple members: members as rows, days as columns.
  return (
    <div className="calendar-roster calendar-scroll">
      <div className="calendar-roster-grid" style={{ "--roster-days": weekDays.length } as React.CSSProperties}>
        <div className="calendar-roster-corner" />
        {weekDays.map((day, index) => (
          <div key={day.format("YYYY-MM-DD")} className="calendar-roster-head">{headers[index]}</div>
        ))}
        {partners.map((partner, partnerIndex) => (
          <PartnerRow
            key={`partner-${partner?.user?.id ?? partnerIndex}`}
            partner={partner}
            weekDays={weekDays}
            todayKey={todayKey}
            shiftSchedule={shiftSchedule}
            onEmptySlotClick={onEmptySlotClick}
            onEventClick={onEventClick}
            setSelectedEvent={setSelectedEvent}
          />
        ))}
      </div>
    </div>
  );
};

const MemoWeekView = React.memo(WeekView);
export default MemoWeekView;
